import { useState, useEffect, useCallback } from 'react';
import SyncService from '../services/SyncService';

const STORAGE_KEY = 'quran_bookmarks';

export const useBookmarks = () => {
  const [bookmarks, setBookmarks] = useState([]);

  // Load bookmarks from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setBookmarks(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading bookmarks:', error);
    }
  }, []);

  // Save bookmarks to localStorage
  const saveBookmarks = useCallback((newBookmarks) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newBookmarks));
      setBookmarks(newBookmarks);
      SyncService.scheduleSync(); // ← cloud sync trigger
    } catch (error) {
      console.error('Error saving bookmarks:', error);
    }
  }, []);

  // Add a bookmark
  const addBookmark = useCallback((bookmark) => {
    const newBookmark = {
      ...bookmark,
      id: `${bookmark.type}-${bookmark.surahNumber}${bookmark.ayahNumber ? `-${bookmark.ayahNumber}` : ''}`,
      timestamp: Date.now(),
    };

    const updated = [...bookmarks.filter(b => b.id !== newBookmark.id), newBookmark];
    saveBookmarks(updated);
  }, [bookmarks, saveBookmarks]);

  // Remove a bookmark
  const removeBookmark = useCallback((id) => {
    const updated = bookmarks.filter(b => b.id !== id);
    saveBookmarks(updated);
  }, [bookmarks, saveBookmarks]);

  // Check if something is bookmarked
  const isBookmarked = useCallback((surahNumber, ayahNumber = null) => {
    const id = ayahNumber
      ? `ayah-${surahNumber}-${ayahNumber}`
      : `surah-${surahNumber}`;
    return bookmarks.some(b => b.id === id);
  }, [bookmarks]);

  // Toggle bookmark
  const toggleBookmark = useCallback((bookmark) => {
    const id = bookmark.ayahNumber
      ? `ayah-${bookmark.surahNumber}-${bookmark.ayahNumber}`
      : `surah-${bookmark.surahNumber}`;

    if (isBookmarked(bookmark.surahNumber, bookmark.ayahNumber)) {
      removeBookmark(id);
    } else {
      addBookmark({
        ...bookmark,
        type: bookmark.ayahNumber ? 'ayah' : 'surah',
      });
    }
  }, [isBookmarked, addBookmark, removeBookmark]);

  // Get bookmarks grouped by surah
  const getGroupedBookmarks = useCallback(() => {
    const grouped = {};

    bookmarks.forEach(bookmark => {
      if (!grouped[bookmark.surahNumber]) {
        grouped[bookmark.surahNumber] = {
          surahName: bookmark.surahName,
          surahEnglishName: bookmark.surahEnglishName,
          ayahs: [],
        };
      }

      if (bookmark.ayahNumber) {
        grouped[bookmark.surahNumber].ayahs.push(bookmark);
      }
    });

    return grouped;
  }, [bookmarks]);

  return {
    bookmarks,
    addBookmark,
    removeBookmark,
    isBookmarked,
    toggleBookmark,
    getGroupedBookmarks,
    hasBookmarks: bookmarks.length > 0,
  };
};

